import React, { FC } from 'react';
import { Field } from 'react-final-form';
import { SubmissionErrors, ValidationErrors } from 'final-form';
import classNames from 'classnames';

import { ICountry } from '../store/actions';
import { Heading, Note } from 'components/ui';
import { IAppCountryForm } from './AppPricing';
import { priceWithMax2DecimalsRegex, t } from 'utils';
import AppPricingCountryForm from './AppPricingFormCountry';
import { RadiobuttonWrapper, SwitchInputWrapper, NumberInputWrapper } from 'components/forms';

import styles from './AppPricing.module.scss';
export interface IAppPricingFormPayPerUsage {
  form: any;
  countries: ICountry[];
  errors: ValidationErrors;
  isFormDisabled: boolean;
  values: { [key: string]: any };
  submitErrors: SubmissionErrors;
  dirtyFieldsSinceLastSubmit: { [key: string]: boolean };
}

const AppPricingFormPayPerUsage: FC<IAppPricingFormPayPerUsage> = (props) => {
  const {
    form,
    values,
    errors,
    countries,
    submitErrors,
    isFormDisabled,
    dirtyFieldsSinceLastSubmit,
  } = props;
  const { allowedCountries, payPerUsageType, copyPrices } = values;
  const firstCountry = allowedCountries[0];
  const {
    usageMinUnitPrice: firstCountryUsageMinUnitPrice,
    usageMaxUnitPrice: firstCountryUsageMaxUnitPrice,
  } = firstCountry;
  const allowedCountriesCount = allowedCountries?.length;
  // get all countries' currencies
  const allowedCountriesCurrencies = allowedCountries.reduce(
    (allCurrencies: [string], countryWithCurrency: IAppCountryForm) =>
      allCurrencies.includes(countryWithCurrency.currencyCode)
        ? allCurrencies
        : [...allCurrencies, countryWithCurrency.currencyCode],
    []
  );
  const isCountriesWithEUR = allowedCountriesCurrencies.includes('EUR');
  const isCountriesWithUSD = allowedCountriesCurrencies.includes('USD');

  return (
    <div className={styles.box}>
      <Heading noTopPadding>{t('pricing.payPerUsage.type.title')}</Heading>
      <Field
        label='pricing.payPerUsage.fixed'
        name='payPerUsageType'
        value='fixed'
        type='radio'
        initialValue='fixed'
        disabled={isFormDisabled}
        component={RadiobuttonWrapper}
      />
      <Note className='mt-1 mb-2'>{t('pricing.payPerUsage.fixed.hint')}</Note>
      <Field
        label='pricing.payPerUsage.variable'
        name='payPerUsageType'
        value='variable'
        type='radio'
        initialValue='fixed'
        disabled={isFormDisabled}
        component={RadiobuttonWrapper}
      />
      <Note className='mt-1 mb-2'>{t('pricing.payPerUsage.variable.hint')}</Note>
      <Heading className={styles.heading}>{t('pricing.payPerUsage.pricing.title')}</Heading>
      {allowedCountriesCount > 1 && (
        <>
          <Field
            name='copyPrices'
            className='mb-2'
            label='pricing.payPerUsage.copy.label'
            type='checkbox'
            disabled={isFormDisabled}
            component={SwitchInputWrapper}
          />
          {copyPrices && (
            <>
              <div
                className={classNames(styles['ta-copy-prices'], {
                  [styles['ta-copy-prices__fixed']]: payPerUsageType === 'fixed',
                  [styles['ta-copy-prices__fixed__single']]:
                    !isCountriesWithEUR || !isCountriesWithUSD,
                })}
              >
                {payPerUsageType === 'fixed' && (
                  <>
                    {isCountriesWithEUR && (
                      <Field
                        name='copyPricesUnitPriceEUR'
                        currencySymbol='€'
                        error={
                          (!dirtyFieldsSinceLastSubmit?.copyPricesUnitPriceEUR &&
                            submitErrors?.copyPricesUnitPriceEUR) ||
                          errors?.copyPricesUnitPriceEUR
                        }
                        label='pricing.payPerUsage.price.label'
                        placeholder='pricing.payPerUsage.price.placeholder'
                        validate={(value: string = '', allValues, fieldState) => {
                          const { touched } = fieldState ?? {};
                          if (!touched || (touched && value.length === 0)) return undefined;
                          const formattedValue = `${value}`;
                          let isPriceValid = false;
                          if (
                            formattedValue.match(priceWithMax2DecimalsRegex) &&
                            formattedValue !== '0.00'
                          ) {
                            isPriceValid = true;
                          }
                          return isPriceValid ? undefined : t('errors.price.invalid');
                        }}
                        changeValue={form?.change}
                        component={NumberInputWrapper}
                        disabled={isFormDisabled}
                        required
                        initialValue={firstCountryUsageMinUnitPrice}
                      />
                    )}
                    {isCountriesWithUSD && (
                      <Field
                        name='copyPricesUnitPriceUSD'
                        currencySymbol='$'
                        error={
                          (!dirtyFieldsSinceLastSubmit?.copyPricesUnitPriceUSD &&
                            submitErrors?.copyPricesUnitPriceUSD) ||
                          errors?.copyPricesUnitPriceUSD
                        }
                        label='pricing.payPerUsage.price.label'
                        placeholder='pricing.payPerUsage.price.placeholder'
                        validate={(value: string = '', allValues, fieldState) => {
                          const { touched } = fieldState ?? {};
                          if (!touched || (touched && value.length === 0)) return undefined;
                          const formattedValue = `${value}`;
                          let isPriceValid = false;
                          if (
                            formattedValue.match(priceWithMax2DecimalsRegex) &&
                            formattedValue !== '0.00'
                          ) {
                            isPriceValid = true;
                          }
                          return isPriceValid ? undefined : t('errors.price.invalid');
                        }}
                        changeValue={form?.change}
                        component={NumberInputWrapper}
                        disabled={isFormDisabled}
                        required
                        initialValue={firstCountryUsageMinUnitPrice}
                      />
                    )}
                  </>
                )}
                {payPerUsageType === 'variable' && (
                  <>
                    {isCountriesWithEUR && (
                      <>
                        <Field
                          name='copyPricesMinEUR'
                          currencySymbol='€'
                          error={
                            (!dirtyFieldsSinceLastSubmit?.copyPricesMinEUR &&
                              submitErrors?.copyPricesMinEUR) ||
                            errors?.copyPricesMinEUR
                          }
                          label='pricing.payPerUsage.minPrice.label'
                          placeholder='pricing.payPerUsage.minPrice.placeholder'
                          validate={(
                            value: string = '',
                            allValues: { [key: string]: any },
                            fieldState
                          ): string | undefined => {
                            const { touched } = fieldState ?? {};
                            let error; // no error
                            if (!touched || (touched && value.length === 0)) return error;
                            const { copyPricesMaxEUR } = allValues ?? {};
                            const formattedValue = `${value}`;
                            const formattedMaxPrice: string = copyPricesMaxEUR ?? 0;
                            const numberFormattedValue = parseFloat(formattedValue);
                            const numberPayPerUsageMaxPrice = parseFloat(`${formattedMaxPrice}`);
                            const isBiggerThanMaxPrice =
                              numberFormattedValue > numberPayPerUsageMaxPrice;
                            const isSameAsMaxPrice =
                              numberFormattedValue === numberPayPerUsageMaxPrice;
                            if (
                              !formattedValue.match(priceWithMax2DecimalsRegex) ||
                              formattedValue === '0.00'
                            )
                              error = t('errors.price.invalid');
                            if (isBiggerThanMaxPrice) error = t('errors.price.minBiggerThanMax');
                            if (isSameAsMaxPrice) error = t('errors.payPerUsage.samePrice');
                            return error;
                          }}
                          changeValue={form?.change}
                          component={NumberInputWrapper}
                          disabled={isFormDisabled}
                          required
                          initialValue={firstCountryUsageMinUnitPrice}
                        />
                        <Field
                          name='copyPricesMaxEUR'
                          currencySymbol='€'
                          error={
                            (!dirtyFieldsSinceLastSubmit?.copyPricesMaxEUR &&
                              submitErrors?.copyPricesMaxEUR) ||
                            errors?.copyPricesMaxEUR
                          }
                          label='pricing.payPerUsage.maxPrice.label'
                          placeholder='pricing.payPerUsage.maxPrice.placeholder'
                          validate={(
                            value: string = '',
                            allValues: { [key: string]: any },
                            fieldState
                          ): string | undefined => {
                            const { touched } = fieldState ?? {};
                            if (!touched || (touched && value.length === 0)) return undefined;
                            const { copyPricesMinEUR } = allValues;
                            const formattedValue = `${value}`;
                            const formattedMinPrice: string = copyPricesMinEUR ?? 0;
                            const numberFormattedValue = parseFloat(formattedValue);
                            const numberPayPerUsageMinPrice = parseFloat(`${formattedMinPrice}`);
                            const isLessThanMinPrice =
                              numberFormattedValue < numberPayPerUsageMinPrice;
                            const isSameAsMinPrice =
                              numberFormattedValue === numberPayPerUsageMinPrice;
                            let error; // no error
                            if (
                              !formattedValue.match(priceWithMax2DecimalsRegex) ||
                              formattedValue === '0.00'
                            )
                              error = t('errors.price.invalid');
                            if (isLessThanMinPrice) error = t('errors.price.maxLessThanMin');
                            if (isSameAsMinPrice) error = t('errors.payPerUsage.samePrice');
                            return error;
                          }}
                          changeValue={form?.change}
                          component={NumberInputWrapper}
                          disabled={isFormDisabled}
                          required
                          initialValue={firstCountryUsageMaxUnitPrice}
                        />
                      </>
                    )}
                    {isCountriesWithUSD && (
                      <>
                        <Field
                          name='copyPricesMinUSD'
                          currencySymbol='$'
                          error={
                            (!dirtyFieldsSinceLastSubmit?.copyPricesMinUSD &&
                              submitErrors?.copyPricesMinUSD) ||
                            errors?.copyPricesMinUSD
                          }
                          label='pricing.payPerUsage.minPrice.label'
                          placeholder='pricing.payPerUsage.minPrice.placeholder'
                          validate={(
                            value: string = '',
                            allValues: { [key: string]: any },
                            fieldState
                          ) => {
                            const { touched } = fieldState ?? {};
                            if (!touched || (touched && value.length === 0)) return undefined;
                            const { copyPricesMaxUSD } = allValues;
                            const formattedValue = `${value}`;
                            const formattedMaxPrice: string = copyPricesMaxUSD ?? '0';
                            const numberFormattedValue = parseFloat(formattedValue);
                            const numberPayPerUsageMaxPrice = parseFloat(`${formattedMaxPrice}`);
                            const isBiggerThanMaxPrice =
                              numberFormattedValue > numberPayPerUsageMaxPrice;
                            const isSameAsMaxPrice =
                              numberFormattedValue === numberPayPerUsageMaxPrice;
                            let error; // no error
                            if (
                              !formattedValue.match(priceWithMax2DecimalsRegex) ||
                              formattedValue === '0.00'
                            )
                              error = t('errors.price.invalid');
                            if (isBiggerThanMaxPrice) error = t('errors.price.minBiggerThanMax');
                            if (isSameAsMaxPrice) error = t('errors.payPerUsage.samePrice');
                            return error;
                          }}
                          changeValue={form?.change}
                          component={NumberInputWrapper}
                          disabled={isFormDisabled}
                          required
                          initialValue={firstCountryUsageMinUnitPrice}
                        />
                        <Field
                          name='copyPricesMaxUSD'
                          currencySymbol='$'
                          error={
                            (!dirtyFieldsSinceLastSubmit?.copyPricesMaxUSD &&
                              submitErrors?.copyPricesMaxUSD) ||
                            errors?.copyPricesMaxUSD
                          }
                          label='pricing.payPerUsage.maxPrice.label'
                          placeholder='pricing.payPerUsage.maxPrice.placeholder'
                          validate={(
                            value: string = '',
                            allValues: { [key: string]: any },
                            fieldState
                          ) => {
                            const { touched } = fieldState ?? {};
                            if (!touched || (touched && value.length === 0)) return undefined;
                            const { copyPricesMinUSD } = allValues;
                            const formattedValue = `${value}`;
                            const formattedMinPrice: string = copyPricesMinUSD ?? 0;
                            const numberFormattedValue = parseFloat(formattedValue);
                            const numberPayPerUsageMinPrice = parseFloat(`${formattedMinPrice}`);
                            const isLessThanMinPrice =
                              numberFormattedValue < numberPayPerUsageMinPrice;
                            const isSameAsMinPrice =
                              numberFormattedValue === numberPayPerUsageMinPrice;
                            let error; // no error
                            if (
                              !formattedValue.match(priceWithMax2DecimalsRegex) ||
                              formattedValue === '0.00'
                            )
                              error = t('errors.price.invalid');
                            if (isLessThanMinPrice) error = t('errors.price.maxLessThanMin');
                            if (isSameAsMinPrice) error = t('errors.payPerUsage.samePrice');
                            return error;
                          }}
                          changeValue={form?.change}
                          component={NumberInputWrapper}
                          disabled={isFormDisabled}
                          required
                          initialValue={firstCountryUsageMaxUnitPrice}
                        />
                      </>
                    )}
                  </>
                )}
              </div>
              <Note className='mt-1 mb-2'>{t('pricing.payPerUsage.priceBracket.hint')}</Note>
            </>
          )}
        </>
      )}
      {allowedCountries.map((allowedCountry: IAppCountryForm, i: number) => (
        <AppPricingCountryForm
          i={i}
          form={form}
          key={i} // isn't modified so no need for unique keys
          type='payPerUsage'
          errors={errors} // form errors
          countries={countries}
          copyPrices={copyPrices}
          submitErrors={submitErrors}
          isPricesDisabled={copyPrices}
          allowedCountry={allowedCountry}
          isFormDisabled={isFormDisabled}
          payPerUsageType={payPerUsageType}
          allowedCountries={allowedCountries}
          dirtyFieldsSinceLastSubmit={dirtyFieldsSinceLastSubmit}
        />
      ))}
    </div>
  );
};

export default AppPricingFormPayPerUsage;
